import { take, fork, put, race, delay, spawn, call, all } from 'redux-saga/effects'
import { handleRequest } from './requests'

import i18n from 'config/i18n'
import { showSnackBar, showToast } from '../services/toast'
import { generateUuid } from '../services/uuid'
import {
    SubmissionsActions,
    SubmissionsListRequestPayload,
    SubmissionsPageListRequestPayload,
    BatchSubmissionsPageListRequestPayload,
    DropoutsListRequestPayload,
    RejectedListRequestPayload,
    saveSubmissionsList,
    updateCaptionReviewInSubmissionsList,
    replaceSubmissionsList,
    updateCaptionReviewInBatchSubmissionsList,
    replaceBatchSubmissionsList,
    RejectSubmissionPayload,
    ApproveSubmissionPayload,
    RequestEditsForSubmissionPayload,
    ReviewPendingActionPayload,
    UpdateSubmissionPerformancePayload,
    PaySubmissionPayload,
    MassPaySubmissionPayload,
    RejectSubmissionPayoutPayload,
    DiscussSubmissionPayload,
    VoteSubmissionPayload,
    updateVote,
    updateDiscussList,
    UpdateDiscussionPayload,
    VotesState,
    MassSubmissionPayload,
    MassSubmissionResult,
    UpdateCompletedSchedulePayload,
    updateSchedule,
    MassPayoutRequest,
    saveMassPayoutProgress,
    MassPayoutResult,
    saveMassPayoutResult,
    saveEditRequestHistoryList,
    getSubmissionsPageList as getSubmissionsPageListAction,
    getBatchSubmissionsPageList as getBatchSubmissionsPageListAction,
    SUBMISSION_VIEW_ENUM,
    ExtendFirstStepDeadlineRequestPayload,
    ReviewCaptionRequestPayload,
} from '../state/submissions'
import { REVIEW_MODAL_TYPE, setReviewModalType, setReviewModalOpen, resetMultiSelectSetting } from '../state/review'
import {
    getCampaignSummary,
    updateCampaignPerformance,
    updatePerformanceDisqualify,
    updatePerformancePay,
} from '../state/campaigns'
import { setResponse, STATE_KEYS } from '../state/requests'
import {
    getSubmissionsPageList,
    getBatchSubmissionsPageList,
    getDropoutsList,
    getRejectedList,
    SubmissionsListResponse,
    rejectSubmission,
    rejectBatchSubmission,
    RejectSubmissionResponse,
    approveSubmission,
    ApproveSubmissionResponse,
    getDiscussSubmission,
    createDiscussSubmission,
    updateDiscussSubmission,
    deleteDiscussSubmission,
    DiscussSubmissionResponse,
    voteSubmission,
    requestEditsForSubmission,
    RequestEditsForSubmissionResponse,
    reviewPendingAction,
    ReviewPendingActionResponse,
    updateSubmissionPerformance,
    paySubmission,
    rejectSubmissionPayout,
    massSubmission,
    MassSubmissionResponse,
    updateCompletedSchedule,
    UpdateCompletedScheduleResponse,
    BatchSubmissionsListResponse,
    RequestExportSubmissionsPayload,
    exportSubmissions,
    MassPayoutSubmissionByUsernameResponse,
    MassPayoutSubmissionError,
    massPayoutSubmission,
    GetEditRequestHistoryListRequestPayload,
    GetEditRequestHistoryListResponse,
    getEditRequestHistoryList,
    getBatchSubmissionsPageFilters,
    getSubmissionsPageFilters,
    extendFirstStepDeadline,
    reviewCaption,
} from '../services/api/submissions'
import { checkForError } from '../services/api/errors'
import { closeModal } from 'state/constants'
import { savePartiposterProfiles } from 'state/partiposter'
import { separatePartiposterProfile } from 'services/partiposter'
import { MAX_APPROVE_CHUNK } from 'components/Shared/ListApprovePopUp'
import { saveSubmissionFilters } from 'state/filter'
import { SubmissionFiltersResponse } from 'services/api/campaigns'

export function* handleGetSubmissionsPageList(payload: SubmissionsPageListRequestPayload): any {
    yield handleRequest<SubmissionsListRequestPayload, SubmissionsListResponse>({
        stateKey: STATE_KEYS.SubmissionsList,
        payload: payload,
        ApiHandler: getSubmissionsPageList,
        *onSuccess(response) {
            if (!response.data) return

            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            yield put(
                replaceSubmissionsList({
                    data,
                }),
            )
        },
    })
}

export function* watchSubmissionsPageListRequestFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.SUBMISSIONS_PAGE_LIST_REQUEST)
        yield race({
            task: call(handleGetSubmissionsPageList, action.payload),
            cancelAction: take(SubmissionsActions.SUBMISSIONS_PAGE_LIST_CANCEL),
        })
    }
}

export function* handleGetSubmissionsPageFilters(payload: SubmissionsListRequestPayload): any {
    yield handleRequest<SubmissionsListRequestPayload, SubmissionFiltersResponse>({
        stateKey: STATE_KEYS.SkipLoading,
        payload,
        ApiHandler: getSubmissionsPageFilters,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveSubmissionFilters(response.data.data.results))
            }
        },
    })
}

export function* watchSubmissionsPageFiltersRequestFlow(): any {
    // Run filter request together with the list request
    while (true) {
        const action = yield take(SubmissionsActions.SUBMISSIONS_PAGE_LIST_REQUEST)
        yield fork(handleGetSubmissionsPageFilters, action.payload)
    }
}

export function* handleGetBatchSubmissionsPageList(payload: BatchSubmissionsPageListRequestPayload): any {
    yield handleRequest<BatchSubmissionsPageListRequestPayload, BatchSubmissionsListResponse>({
        stateKey: STATE_KEYS.SubmissionsList,
        payload: payload,
        ApiHandler: getBatchSubmissionsPageList,
        *onSuccess(response) {
            if (!response.data) return

            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            yield put(
                replaceBatchSubmissionsList({
                    count: response.data.data.count,
                    data,
                }),
            )
        },
    })
}

export function* watchBatchSubmissionsPageListRequestFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.BATCH_SUBMISSIONS_PAGE_LIST_REQUEST)
        yield race({
            task: call(handleGetBatchSubmissionsPageList, action.payload),
            cancelAction: take(SubmissionsActions.BATCH_SUBMISSIONS_PAGE_LIST_CANCEL),
        })
    }
}

export function* handleGetBatchSubmissionsPageFilters(payload: BatchSubmissionsPageListRequestPayload): any {
    yield handleRequest<BatchSubmissionsPageListRequestPayload, SubmissionFiltersResponse>({
        stateKey: STATE_KEYS.SkipLoading,
        payload,
        ApiHandler: getBatchSubmissionsPageFilters,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveSubmissionFilters(response.data.data.results))
            }
        },
    })
}

export function* watchGetCampaignPerformanceFiltersRequestFlow(): any {
    // Run filter request together with the list request
    while (true) {
        const action = yield take(SubmissionsActions.BATCH_SUBMISSIONS_PAGE_LIST_REQUEST)
        yield fork(handleGetBatchSubmissionsPageFilters, action.payload)
    }
}

export function* handleGetDropoutsList(payload: DropoutsListRequestPayload): any {
    yield handleRequest<DropoutsListRequestPayload, SubmissionsListResponse>({
        stateKey: STATE_KEYS.DropoutsList,
        payload,
        ApiHandler: getDropoutsList,
        *onSuccess(response) {
            if (!response.data) return

            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            yield put(
                saveSubmissionsList({
                    data,
                }),
            )
        },
    })
}

export function* watchDropoutsListRequestFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.DROPOUTS_LIST_REQUEST)
        yield fork(handleGetDropoutsList, action.payload)
    }
}

export function* handleGetRejectedList(payload: RejectedListRequestPayload): any {
    yield handleRequest<RejectedListRequestPayload, SubmissionsListResponse>({
        stateKey: STATE_KEYS.RejectedList,
        payload,
        ApiHandler: getRejectedList,
        *onSuccess(response) {
            if (!response.data) return

            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            yield put(
                saveSubmissionsList({
                    data,
                }),
            )
        },
    })
}

export function* watchRejectedListRequestFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.REJECTED_LIST_REQUEST)
        yield fork(handleGetRejectedList, action.payload)
    }
}

export function* handleRejectSubmission(payload: RejectSubmissionPayload): any {
    const undoId = `REJECT_SUBMISSION_${
        payload.type === REVIEW_MODAL_TYPE.BATCH_SINGLE_REJECT ? payload.submissionUuid : payload.submissionProgressUuid
    }`
    showToast({
        title: i18n.t('successMessage.submissionRejectedTitle', 'Partiposter rejected!'),
        message: i18n.t(
            'successMessage.submissionRejectedSubtitle',
            'They will not be able to join this campaign again',
        ),
        undoAction: { countdown: 3000, undoId },
        toastType: 'warning',
    })
    const { undoRequested, undoNotRequested } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
    })
    if (undoRequested) {
        showSnackBar({
            title: i18n.t('successMessage.rejectionReversed', 'Rejection has been reversed!'),
            type: 'positive',
        })
    }
    if (undoNotRequested) {
        yield handleRequest<RejectSubmissionPayload, RejectSubmissionResponse>({
            stateKey: STATE_KEYS.RejectSubmission,
            payload,
            ApiHandler:
                payload.type === REVIEW_MODAL_TYPE.BATCH_SINGLE_REJECT ? rejectBatchSubmission : rejectSubmission,
            *onSuccess(response) {
                const { campaignUuid, getSubmissionListPayload } = payload
                showSnackBar({
                    title: i18n.t('successMessage.submissionRejectedTitle', 'Partiposter rejected!'),
                    type: 'positive',
                })
                if (getSubmissionListPayload.submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                    yield put(
                        getBatchSubmissionsPageListAction({
                            campaignUuid,
                            ...getSubmissionListPayload,
                        }),
                    )
                } else {
                    yield put(
                        getSubmissionsPageListAction({
                            campaignUuid,
                            ...getSubmissionListPayload,
                        }),
                    )
                }
                yield put(getCampaignSummary({ uuid: campaignUuid }))
            },
            *onError(response) {
                showSnackBar({
                    title: i18n.t(
                        'errorMessage.general.somethingWentWrongRejectSubmissionTitle',
                        'Something went wrong while rejecting this submission.',
                    ),
                    type: 'error',
                })
            },
        })
    }
}

export function* watchRejectSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.REJECT_SUBMISSION)
        yield spawn(handleRejectSubmission, action.payload)
    }
}

export function* handleApproveSubmission(payload: ApproveSubmissionPayload): any {
    const undoId = `APPROVE_SUBMISSION_${payload.submissionProgressUuid}`
    showToast({
        title: i18n.t('successMessage.submissionApprovedTitle', 'Submission approved!'),
        message: i18n.t(
            'successMessage.partiposterToContinueAfterSubmissionSubtitle',
            'The Partiposter will be able to proceed to the next step of the campaign (if available)',
        ),
        undoAction: { countdown: 3000, undoId },
        toastType: 'positive',
    })
    const { undoRequested, undoNotRequested } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
    })
    if (undoRequested) {
        showSnackBar({
            title: i18n.t('successMessage.approvalReversed', 'Approval has been reversed!'),
            type: 'positive',
        })
    }
    if (undoNotRequested) {
        yield handleRequest<ApproveSubmissionPayload, ApproveSubmissionResponse>({
            stateKey: STATE_KEYS.ApproveSubmission,
            payload,
            ApiHandler: approveSubmission,
            *onSuccess(response) {
                const { campaignUuid, getSubmissionListPayload } = payload
                showSnackBar({
                    title: i18n.t('successMessage.submissionApprovedTitle', 'Submissions approved'),
                    type: 'positive',
                })

                yield put(getCampaignSummary({ uuid: campaignUuid }))
                if (getSubmissionListPayload.submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                    yield put(
                        getBatchSubmissionsPageListAction({
                            campaignUuid,
                            ...getSubmissionListPayload,
                        }),
                    )
                } else {
                    yield put(
                        getSubmissionsPageListAction({
                            campaignUuid,
                            ...getSubmissionListPayload,
                        }),
                    )
                }
            },
            *onError(response) {
                const isNotInBatchError = checkForError(
                    response,
                    'SUBMISSION_PROGRESS_INVALID',
                    'SUBMISSION_NOT_IN_BATCH',
                )
                if (isNotInBatchError) {
                    yield put(setReviewModalType(REVIEW_MODAL_TYPE.IN_WAITING_LIST))
                    yield put(setReviewModalOpen(true))
                } else {
                    if (checkForError(response, 'SUBMISSION_PROGRESS_INVALID', 'NEXT_STEP_SCHEDULE_OVER')) {
                        showSnackBar({
                            title: i18n.t(
                                'submission.review.deadlineExpiredInstruction',
                                "The Partiposter will not be able to progress to the next step because it's deadline is over.",
                            ),
                            type: 'error',
                        })
                    } else {
                        showSnackBar({
                            title: i18n.t('errorMessage.general.unknownError'),
                            type: 'error',
                        })
                    }
                }
            },
        })
    }
}

export function* watchApproveSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.APPROVE_SUBMISSION)
        yield spawn(handleApproveSubmission, action.payload)
    }
}

export function* handleUpdateCompletedSchedule(payload: UpdateCompletedSchedulePayload): any {
    yield handleRequest<UpdateCompletedSchedulePayload, UpdateCompletedScheduleResponse>({
        stateKey: STATE_KEYS.UpdateCompletedSchedule,
        payload,
        ApiHandler: updateCompletedSchedule,
        *onSuccess(response) {
            yield put(
                updateSchedule({
                    data: response.data !== undefined ? response.data.data : undefined,
                    submissionUuid: payload.submissionUuid,
                }),
            )
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t(
                    'errorMessage.general.somethingWentWrongUpdateScheduleTitle',
                    'Something went wrong while update your schedule',
                ),
                type: 'error',
            })
        },
    })
}

export function* watchUploadCompletedScheduleFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.UPDATE_COMPLETED_SCHEDULE)
        yield spawn(handleUpdateCompletedSchedule, action.payload)
    }
}

export function* handleMassSubmission(payload: MassSubmissionPayload): any {
    const undoId = `MASS_SUBMISSION_${generateUuid()}`
    let confirmTitle: string, confirmMsg: string, sucTitle: string, recallTitle: string, errTitle: string
    switch (payload.action_name) {
        case 'approve': {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.confirmApprovalsSubtitle', {
                defaultValue:
                    '{{count}} submissions will be under review before the Partiposters are approved. Click Recall to cancel approvals.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.massSubmissionApprovedTitle', 'Submission Approved')

            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')

            break
        }
        case 'request_edit': {
            confirmTitle = i18n.t('warningMessage.confirmEditRequestsTitle', 'Confirming Request for Edits')
            confirmMsg = i18n.t('warningMessage.confirmEditRequestsSubtitle', {
                defaultValue:
                    '{{count}} submissions will be under review before edits are requested from the Partiposters are approved. Click Recall to cancel reqiest.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.massSubmissionEditRequestedTitle', 'Edits requested')

            recallTitle = i18n.t(
                'successMessage.requestEditRecalled',
                'Requesting for edits recalled and Partiposters remain in their current status',
            )
            break
        }
        case 'reject': {
            confirmTitle = i18n.t('warningMessage.confirmRejectionsTitle', 'Confirming Rejections')
            confirmMsg = i18n.t('warningMessage.confirmRejectionsSubtitle', {
                defaultValue:
                    '{{count}} Partiposters be under review before Partiposters are rejected. They will not be able to join the campaign again. Click Recall to cancel rejection.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.massSubmissionRejectedTitle', 'Submission Rejected')

            recallTitle = i18n.t('successMessage.rejectionRecalledTitle', 'Rejection Recalled')

            break
        }
        case 'approve_pending_action': {
            confirmTitle = i18n.t('warningMessage.confirmAcceptStatusTitle', 'Confirming Accept Status')
            confirmMsg = i18n.t('warningMessage.confirmAcceptStatusSubtitle', {
                defaultValue: '{{count}} submissions will be accepted status. Click Recall to cancel this action.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.massSubmissionStatusAcceptedTitle', 'Status Accepted')

            recallTitle = i18n.t('successMessage.statusRecalledTitle', 'Status Recalled')

            break
        }
        case 'reject_pending_action': {
            confirmTitle = i18n.t(
                'warningMessage.massSubmissionSendBackToPendingTitle',
                'Confirming Send Back To Pending',
            )
            confirmMsg = i18n.t('warningMessage.massSubmissionSendBackToPendingSubtitle', {
                defaultValue: '{{count}} submissions will be sent back to pending. Click Recall to cancel this action.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t(
                'successMessage.massSubmissionSentBackToPendingTitle',
                'Send Back to Pending Successfully',
            )
            recallTitle = i18n.t('successMessage.recallSendBackToPendingTitle', 'Send Back To Pending Recalled')

            break
        }
        default: {
            confirmTitle = 'confirm title'
            confirmMsg = 'confirm message'
            sucTitle = 'success title'
            recallTitle = 'recall title'
            break
        }
    }

    showToast({
        title: confirmTitle,
        message: confirmMsg,
        undoAction: { countdown: 3000, undoId },
        toastType: 'warning',
    })
    const { undoRequested, undoNotRequested, skipUndo } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
        skipUndo: take((action: any) => action.type === 'requests/SKIPUNDO' && action.payload.undoId === undoId),
    })
    if (undoRequested) {
        showSnackBar({
            title: recallTitle,
            type: 'positive',
        })
    }
    if (undoNotRequested || skipUndo) {
        yield handleRequest<MassSubmissionPayload, MassSubmissionResponse>({
            stateKey: STATE_KEYS.MassSubmission,
            payload,
            ApiHandler: massSubmission,
            *onSuccess(response) {
                const massSubmissionResult: MassSubmissionResult = {
                    ...payload,
                    data: response.data !== undefined ? response.data.data : undefined,
                }
                const { campaignUuid, getSubmissionListPayload, action_name } = payload

                if (massSubmissionResult.data.errors.length > 0) {
                    switch (action_name) {
                        case 'approve': {
                            errTitle = i18n.t('errorMessage.general.submissionsApprovedAndFailedCountTitle', {
                                successCount: massSubmissionResult.data.success_count,
                                errorCount: massSubmissionResult.data.errors.length,
                            })
                            break
                        }
                        case 'request_edit': {
                            errTitle = i18n.t('errorMessage.general.submissionsEditRequestedAndFailedCountTitle', {
                                successCount: massSubmissionResult.data.success_count,
                                errorCount: massSubmissionResult.data.errors.length,
                            })

                            break
                        }
                        case 'reject': {
                            errTitle = i18n.t('errorMessage.general.submissionsRejectedAndFailedCountTitle', {
                                successCount: massSubmissionResult.data.success_count,
                                errorCount: massSubmissionResult.data.errors.length,
                            })

                            break
                        }
                        case 'approve_pending_action': {
                            errTitle = i18n.t('errorMessage.general.submissionsStatusAcceptedAndFailedCountTitle', {
                                successCount: massSubmissionResult.data.success_count,
                                errorCount: massSubmissionResult.data.errors.length,
                            })
                            break
                        }
                        case 'reject_pending_action': {
                            errTitle = i18n.t('errorMessage.general.submissionsSentBackToPendingAndFailedCountTitle', {
                                successCount: massSubmissionResult.data.success_count,
                                errorCount: massSubmissionResult.data.errors.length,
                            })
                            break
                        }
                        default: {
                            errTitle = 'error title'
                            break
                        }
                    }
                    showSnackBar({
                        title: errTitle,
                        type: 'error',
                    })
                } else {
                    showSnackBar({
                        title: sucTitle,
                        type: 'positive',
                    })
                }
                yield put(resetMultiSelectSetting())
                yield put(getCampaignSummary({ uuid: campaignUuid }))
                yield put(
                    getSubmissionsPageListAction({
                        campaignUuid,
                        ...getSubmissionListPayload,
                    }),
                )
            },
            *onError(response) {
                showSnackBar({
                    title: i18n.t('errorMessage.general.unknownError'),
                    type: 'error',
                })
            },
        })
    }
}

export function* watchMassSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.MASS_SUBMISSION)
        yield spawn(handleMassSubmission, action.payload)
    }
}

export function* handleGetDiscussSubmission(payload: DiscussSubmissionPayload): any {
    yield handleRequest<DiscussSubmissionPayload, DiscussSubmissionResponse>({
        stateKey: STATE_KEYS.GetDiscussSubmission,
        payload,
        ApiHandler: getDiscussSubmission,
        *onSuccess(response) {
            const updateDiscussionPayload: UpdateDiscussionPayload = {
                ...payload,
                actionType: 'get',
                data: response.data !== undefined ? response.data.data : undefined,
            }
            yield put(updateDiscussList(updateDiscussionPayload))
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchGetDiscussSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.GET_DISCUSS_SUBMISSION)
        yield spawn(handleGetDiscussSubmission, action.payload)
    }
}

export function* handleCreateDiscussSubmission(payload: DiscussSubmissionPayload): any {
    yield handleRequest<DiscussSubmissionPayload, DiscussSubmissionResponse>({
        stateKey: STATE_KEYS.CreateDiscussSubmission,
        payload,
        ApiHandler: createDiscussSubmission,
        *onSuccess(response) {
            const updateDiscussionPayload: UpdateDiscussionPayload = {
                ...payload,
                actionType: 'create',
                data: response.data !== undefined ? response.data.data : undefined,
            }
            yield put(updateDiscussList(updateDiscussionPayload))
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchCreateDiscussSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.CREATE_DISCUSS_SUBMISSION)
        yield spawn(handleCreateDiscussSubmission, action.payload)
    }
}

export function* handleUpdateDiscussSubmission(payload: DiscussSubmissionPayload): any {
    yield handleRequest<DiscussSubmissionPayload, DiscussSubmissionResponse>({
        stateKey: STATE_KEYS.UpdateDiscussSubmission,
        payload,
        ApiHandler: updateDiscussSubmission,
        *onSuccess(response) {
            const updateDiscussionPayload: UpdateDiscussionPayload = {
                ...payload,
                actionType: 'update',
                data: response.data !== undefined ? response.data.data : undefined,
            }
            yield put(updateDiscussList(updateDiscussionPayload))
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateDiscussSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.UPDATE_DISCUSS_SUBMISSION)
        yield spawn(handleUpdateDiscussSubmission, action.payload)
    }
}

export function* handleDeleteDiscussSubmission(payload: DiscussSubmissionPayload): any {
    yield handleRequest<DiscussSubmissionPayload, DiscussSubmissionResponse>({
        stateKey: STATE_KEYS.DeleteDiscussSubmission,
        payload,
        ApiHandler: deleteDiscussSubmission,
        *onSuccess(response) {
            const updateDiscussionPayload: UpdateDiscussionPayload = {
                ...payload,
                actionType: 'delete',
                data: undefined,
            }
            yield put(updateDiscussList(updateDiscussionPayload))
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchDeleteDiscussSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.DELETE_DISCUSS_SUBMISSION)
        yield spawn(handleDeleteDiscussSubmission, action.payload)
    }
}

export function* handleVoteSubmission(payload: VoteSubmissionPayload): any {
    yield handleRequest<VoteSubmissionPayload, VotesState>({
        stateKey: STATE_KEYS.VoteSubmission,
        payload,
        ApiHandler: voteSubmission,
        *onSuccess(response) {
            yield put(
                updateVote({
                    data: response.data !== undefined ? response.data.data : undefined,
                    submissionProgressUuid: payload.submissionProgressUuid,
                }),
            )
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t(
                    'errorMessage.general.somethingWentWrongVoteTitle',
                    'Something went wrong while confirming your vote',
                ),
                type: 'error',
            })
        },
    })
}

export function* watchVoteSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.VOTE_SUBMISSION)
        yield spawn(handleVoteSubmission, action.payload)
    }
}

export function* handleRequestEditsForSubmission(payload: RequestEditsForSubmissionPayload): any {
    const undoId = `REQUEST_EDIT_SUBMISSION_${payload.submissionProgressUuid}`
    showToast({
        title: i18n.t('successMessage.editRequestedTitle', 'Edits requested from Partiposter!'),
        message: i18n.t(
            'successMessage.newSubmissionToBePendingAfterEditSubtitle',
            "Hang tight, you will see their new submission in Pending once they're done with their edits.",
        ),
        undoAction: { countdown: 3000, undoId },
        toastType: 'warning',
    })
    const { undoRequested, undoNotRequested } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
    })
    if (undoRequested) {
        showSnackBar({
            title: i18n.t('successMessage.editRequestReversed', 'Request for edits has been reversed!'),
            type: 'positive',
        })
    }
    if (undoNotRequested) {
        yield handleRequest<RequestEditsForSubmissionPayload, RequestEditsForSubmissionResponse>({
            stateKey: STATE_KEYS.RequestEditsForSubmission,
            payload,
            ApiHandler: requestEditsForSubmission,
            *onSuccess(response) {
                const { campaignUuid, getSubmissionListPayload } = payload
                showSnackBar({
                    title: i18n.t('successMessage.editRequestedTitle'),
                    type: 'positive',
                })
                yield put(getCampaignSummary({ uuid: campaignUuid }))
                if (getSubmissionListPayload.submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                    yield put(
                        getBatchSubmissionsPageListAction({
                            campaignUuid,
                            ...getSubmissionListPayload,
                        }),
                    )
                } else {
                    yield put(
                        getSubmissionsPageListAction({
                            campaignUuid,
                            ...getSubmissionListPayload,
                        }),
                    )
                }
            },
            *onError(response) {
                const isNotInBatchError = checkForError(
                    response,
                    'SUBMISSION_PROGRESS_INVALID',
                    'SUBMISSION_NOT_IN_BATCH',
                )
                if (isNotInBatchError) {
                    yield put(setReviewModalType(REVIEW_MODAL_TYPE.IN_WAITING_LIST))
                    yield put(setReviewModalOpen(true))
                } else {
                    if (checkForError(response, 'SUBMISSION_PROGRESS_INVALID', 'SCHEDULE_OVER')) {
                        showSnackBar({
                            title: i18n.t(
                                'errorMessage.businessLogic.cantRequestEditAfterDeadline',
                                'The Partiposter will not be able to resubmit content because the schedule for the current step is over.',
                            ),
                            type: 'error',
                        })
                    } else {
                        showSnackBar({
                            title: i18n.t('errorMessage.general.unknownError'),
                            type: 'error',
                        })
                    }
                }
            },
        })
    }
}

export function* watchRequestEditsForSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.REQUEST_EDITS_FOR_SUBMISSION)
        yield spawn(handleRequestEditsForSubmission, action.payload)
    }
}

export function* handleReviewPendingAction(payload: ReviewPendingActionPayload): any {
    const undoId = `REVIEW_PENDING_ACTION_${payload.submissionUuid}`
    showToast({
        title:
            payload.actionType === 'approve'
                ? i18n.t('successMessage.statusAcceptedTitle', 'Accepted!')
                : i18n.t('successMessage.movedToPendingTitle', 'Rejected'),
        undoAction: { countdown: 3000, undoId },
        toastType: 'positive',
    })
    const { undoRequested, undoNotRequested } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
    })
    if (undoRequested) {
        showSnackBar({
            title: i18n.t('successMessage.actionReversedTitle'),
            type: 'positive',
        })
    }
    if (undoNotRequested) {
        yield handleRequest<ReviewPendingActionPayload, ReviewPendingActionResponse>({
            stateKey: STATE_KEYS.ReviewPendingAction,
            payload,
            ApiHandler: reviewPendingAction,
            *onSuccess(response) {
                const { campaignUuid, getSubmissionListPayload } = payload
                yield put(getCampaignSummary({ uuid: campaignUuid }))
                yield put(
                    getSubmissionsPageListAction({
                        campaignUuid,
                        ...getSubmissionListPayload,
                    }),
                )
            },
            *onError(response) {
                showSnackBar({
                    title: i18n.t('errorMessage.general.unknownError'),
                    type: 'error',
                })
            },
        })
    }
}

export function* watchReviewPendingActionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.REVIEW_PENDING_ACTION)
        yield spawn(handleReviewPendingAction, action.payload)
    }
}

export function* handleUpdateSubmissionPerformance(payload: UpdateSubmissionPerformancePayload): any {
    // yield handleRequest<UpdateSubmissionPerformancePayload, UpdateSubmissionPerformanceResponse>({
    yield handleRequest<UpdateSubmissionPerformancePayload, any>({
        stateKey: STATE_KEYS.UpdateSubmissionPerformance,
        payload,
        ApiHandler: updateSubmissionPerformance,
        *onSuccess(response) {
            yield put(
                updateCampaignPerformance({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
            yield put(
                setResponse({
                    stateKey: STATE_KEYS.UpdateSubmissionPerformance,
                    response: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.businessLogic.somethingWentWrongEditMetricsTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateSubmissionPerformanceFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.UPDATE_SUBMISSION_PERFORMANCE)
        yield spawn(handleUpdateSubmissionPerformance, action.payload)
    }
}

export function* handlePaySubmission(payload: PaySubmissionPayload): any {
    const undoId = `PAY_SUBMISSION_${payload.submissionUuid}`
    showToast({
        title: i18n.t('warningMessage.confirmPayoutTitle'),
        message: i18n.t('warningMessage.confirmCancelPayoutSubtitle'),
        undoAction: { countdown: 3000, undoId },
        toastType: 'warning',
    })
    const { undoRequested, undoNotRequested } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
    })
    if (undoRequested) {
        showSnackBar({
            title: i18n.t('successMessage.payoutCancelledTitle'),
            type: 'positive',
        })
    }
    if (undoNotRequested) {
        yield handleRequest<PaySubmissionPayload, any>({
            stateKey: STATE_KEYS.PaySubmission,
            payload,
            ApiHandler: paySubmission,
            *onSuccess(response) {
                showSnackBar({
                    title: i18n.t('successMessage.payoutConfirmedTitle'),
                    type: 'positive',
                })
                yield put(
                    updatePerformancePay({
                        submissionUuid: payload.submissionUuid,
                    }),
                )
            },
            *onError(response) {
                showSnackBar({
                    title: i18n.t('errorMessage.businessLogic.somethingWentWrongPayoutTitle'),
                    type: 'error',
                })
            },
        })
    }
}

export function* watchPaySubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.PAY_SUBMISSION)
        yield spawn(handlePaySubmission, action.payload)
    }
}

export function* handleRejectSubmissionPayout(payload: RejectSubmissionPayoutPayload): any {
    const undoId = `REJECT_SUBMISSION_PAYOUT_${payload.submissionUuid}`
    showToast({
        title: i18n.t('warningMessage.disqualifyPartiposterTitle'),
        message: i18n.t('warningMessage.disqualifyPartiposterSubtitle'),
        undoAction: { countdown: 3000, undoId },
        toastType: 'positive',
    })
    const { undoRequested, undoNotRequested } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
    })
    if (undoRequested) {
        showSnackBar({
            title: i18n.t('successMessage.cancelDisqualifyTitle'),
            type: 'positive',
        })
    }
    if (undoNotRequested) {
        yield handleRequest<RejectSubmissionPayoutPayload, any>({
            stateKey: STATE_KEYS.RejectSubmissionPayout,
            payload,
            ApiHandler: rejectSubmissionPayout,
            *onSuccess(response) {
                showSnackBar({
                    title: i18n.t('successMessage.partiposterDisqualifiedTitle'),
                    type: 'positive',
                })
                yield put(
                    updatePerformanceDisqualify({
                        submissionUuid: payload.submissionUuid,
                    }),
                )
            },
            *onError(response) {
                showSnackBar({
                    title: i18n.t('errorMessage.businessLogic.somethingWentWrongDisqualifyTitle'),
                    type: 'error',
                })
            },
        })
    }
}

export function* watchRejectSubmissionPayoutFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.REJECT_SUBMISSION_PAYOUT)
        yield spawn(handleRejectSubmissionPayout, action.payload)
    }
}

export function* handleExportSubmissions(payload: RequestExportSubmissionsPayload): any {
    yield handleRequest<RequestExportSubmissionsPayload, any>({
        stateKey: STATE_KEYS.ExportSubmissions,
        payload,
        ApiHandler: exportSubmissions,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.exportSuccessfulTitle'),
                type: 'positive',
            })
            yield put(closeModal())
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.somethingWentWrongExportSubtitle'),
                type: 'error',
            })
            yield put(closeModal())
        },
    })
}

export function* watchExportSubmissionsRequestFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.EXPORT_SUBMISSION_REQUEST)
        yield fork(handleExportSubmissions, action.payload)
    }
}
export function* handleMassPayoutSubmission(payload: MassPaySubmissionPayload): any {
    const { usernames: usernameToChunk, platform, campaignUuid, moveNextStep } = payload
    let processedCount = 0
    const userList = Object.keys(usernameToChunk)
    const totalCount = userList.length
    let errorMessages: MassPayoutSubmissionError[] = []
    const initProgress = { count: processedCount, total: totalCount }
    yield put(saveMassPayoutProgress({ uuid: campaignUuid, progress: initProgress }))
    const chunkedUsernames = userList.reduce<string[][]>((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / MAX_APPROVE_CHUNK)

        if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = [] // start a new chunk
        }

        resultArray[chunkIndex].push(item)

        return resultArray
    }, [])

    yield all(
        chunkedUsernames.map(usernames =>
            handleRequest<MassPayoutRequest, MassPayoutSubmissionByUsernameResponse>({
                stateKey: STATE_KEYS.MassApproveRegistrationByUsername,
                payload: { campaignUuid, usernames, platform, usernameToChunk, moveNextStep },
                ApiHandler: massPayoutSubmission,
                *onSuccess(response) {
                    processedCount += usernames.length
                    const newProgress = { count: processedCount, total: totalCount }
                    yield put(saveMassPayoutProgress({ uuid: campaignUuid, progress: newProgress }))
                    if (response.data !== undefined && response.data.data.errors.length > 0) {
                        errorMessages = [...errorMessages, ...response.data.data.errors]
                    }
                    if (processedCount === userList.length) {
                        const success: MassPayoutResult[] = []
                        const failed: MassPayoutResult[] = []
                        userList.map(username => {
                            const errorUsername = errorMessages.find(item => item.username === username)
                            if (errorUsername) {
                                failed.push({
                                    id: username,
                                    username,
                                    message: errorUsername.error,
                                })
                            } else {
                                success.push({
                                    id: username,
                                    username,
                                })
                            }
                        })
                        yield all(
                            success.map(user => {
                                const submissionId = usernameToChunk[user.username]
                                if (!submissionId) return
                                return put(
                                    updatePerformancePay({
                                        submissionUuid: submissionId,
                                    }),
                                )
                            }),
                        )

                        yield put(saveMassPayoutResult({ campaignUuid, success, failed }))
                        setTimeout(() => {
                            payload.moveNextStep(4)
                        }, 1000)
                    }
                },
                *onError() {
                    yield call(showSnackBar, {
                        title: i18n.t('errorMessage.businessLogic.somethingWentWrongExportSubtitle'),
                        type: 'error',
                    })
                    setTimeout(() => {
                        payload.moveNextStep(1)
                    }, 1000)
                },
            }),
        ),
    )
}
export function* watchMassPayoutSubmissionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.MASS_PAYOUT_SUBMISSION)
        yield spawn(handleMassPayoutSubmission, action.payload)
    }
}

export function* handleGetEditRequestHistoryList(payload: GetEditRequestHistoryListRequestPayload): any {
    yield handleRequest<GetEditRequestHistoryListRequestPayload, GetEditRequestHistoryListResponse>({
        stateKey: STATE_KEYS.GetEditRequestHistoryList,
        payload,
        ApiHandler: getEditRequestHistoryList,
        *onSuccess(response) {
            if (!response.data) return
            yield put(saveEditRequestHistoryList(response.data.data.results))
        },
    })
}
export function* watchGetEditRequestHistoryList(): any {
    while (true) {
        const action = yield take(SubmissionsActions.GET_EDIT_REQUEST_HISTORY_LIST)
        yield spawn(handleGetEditRequestHistoryList, action.payload)
    }
}

export function* handleExtendFirstStepDeadline(payload: ExtendFirstStepDeadlineRequestPayload): any {
    yield handleRequest<ExtendFirstStepDeadlineRequestPayload, any>({
        stateKey: STATE_KEYS.ExtendFirstStepDeadline,
        payload,
        ApiHandler: extendFirstStepDeadline,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.extendDeadline.title', {
                    stepName: payload.stepName,
                    newStep1EndDate: payload.newStep1EndDate,
                }),
                type: 'positive',
            })
            if (payload.onSuccessCallback) {
                yield payload.onSuccessCallback()
            }
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}
export function* watchExtendFirstStepDeadline(): any {
    while (true) {
        const action = yield take(SubmissionsActions.EXTEND_FIRST_STEP_DEADLINE_REQUEST)
        yield spawn(handleExtendFirstStepDeadline, action.payload)
    }
}

export function* handleReviewCaption(payload: ReviewCaptionRequestPayload): any {
    yield handleRequest<ReviewCaptionRequestPayload, any>({
        stateKey: STATE_KEYS.ReviewCaption,
        payload,
        ApiHandler: reviewCaption,
        *onSuccess(response) {
            if (!response.data) return
            if (payload.selectedSubmissionView == SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                yield put(
                    updateCaptionReviewInBatchSubmissionsList({
                        data: response.data.data,
                        submissionProgressUuid: payload.submissionProgressUuid,
                    }),
                )
            } else {
                yield put(
                    updateCaptionReviewInSubmissionsList({
                        data: response.data.data,
                        submissionProgressUuid: payload.submissionProgressUuid,
                    }),
                )
            }
            yield call(() => payload.updateLoadingCallback(payload.submissionProgressUuid))
            yield call(() => payload.retrySuccessCallback())
        },
        *onError(response) {
            showSnackBar({
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}
export function* watchReviewCaptionFlow(): any {
    while (true) {
        const action = yield take(SubmissionsActions.REVIEW_CAPTION)
        yield spawn(handleReviewCaption, action.payload)
    }
}
